import type { ChatTurn } from "./turn";

export interface ThreadSummary {
  title: string;
  turns: number;
  userTurns: number;
  assistantTurns: number;
  streaming: boolean;
  approxTokens: number;
}

export function estimateTokens(text: string | undefined | null): number {
  // Defensive against undefined/null. Wire fields with
  // `#[serde(skip_serializing_if = "String::is_empty")]` arrive
  // missing rather than as "", and we sum across many turns — one
  // bad input shouldn't crash the chat summary memo.
  if (!text) return 0;
  return Math.max(1, Math.ceil(text.length / 4));
}

// Sentinel markers wrapping the Exa results that `/search` splices into the
// user message before the LLM call. Written in ChatPage.send(), read back
// here — keep the two in sync (they are the only copies).
// Sentinels are delimiters, not labels — but they are spliced into the
// message the MODEL reads, so a provider name in them is a claim about where
// the grounding came from. The originals named Exa; once a second provider
// existed that claim became false on TinyFish turns, so new blocks use the
// neutral forms and the provider travels in the payload instead.
//
// The legacy forms stay readable forever: every row already persisted uses
// them, and dropping support would render the raw block as those threads'
// question bubbles and titles. The end marker never named a provider.
const SEARCH_CONTEXT_START = "--- Web Search Results ---";
const SEARCH_CONTEXT_START_LEGACY = "--- Web Search Results via Exa ---";
const SEARCH_CONTEXT_END = "--- End Web Search Results ---";
// Opens the machine-readable citation payload that `buildSearchContext`
// appends after the human-readable results. Defused inside result text like
// the other two, which is what makes the payload unforgeable.
const SEARCH_SOURCES_START = "--- Search Sources ---";
const SEARCH_SOURCES_START_LEGACY = "--- Exa Sources ---";

// Spliced into the block the model reads on a search turn. gpt-oss-120b
// has ignored longer honesty directives; keep this one line and specific
// to the failure we measured (historical table row cited as "current").
const SEARCH_CONFLICT_HINT =
  "When sources disagree on a number or date, say so and prefer the official, most recently dated figure. Do not treat a historical table row as current.";

const BROWSE_CONTEXT_START = "--- TinyFish Agent Results ---";
const BROWSE_CONTEXT_END = "--- End TinyFish Agent Results ---";
const BROWSE_SOURCES_START = "--- Agent Sources ---";
const BROWSE_HINT =
  "Use only these extracted results to answer the user. When sources disagree, say so. Do not invent prices, product names, or limits.";
export const BROWSE_CONTEXT_PROVIDER = "tinyfish_agent";

// Defuse the sentinels inside text that will be spliced *between* them.
// Result titles and bodies are arbitrary fetched page content, so without
// this a page that quotes either marker makes the block ambiguous: `indexOf`
// truncated it mid-results, `lastIndexOf` swallowed the user's question.
// Neutralising at write time removes the ambiguity at its source, so the
// scan below can be a simple bounded one.
// The replacements deliberately do NOT end in `---`. An earlier version did,
// and `split().join()` then spliced a fresh marker across the boundary:
//   "--- End Web Search Results --- End Web Search Results ---"
// neutralised to "…(quoted) --- End Web Search Results ---", which still
// contains an exact sentinel. Since neither replacement ends in a prefix of
// either marker, no marker can span a join boundary.
export function neutralizeSearchMarkers(text: string): string {
  return text
    .split(SEARCH_CONTEXT_START)
    .join("--- Web Search Results (quoted)")
    .split(SEARCH_CONTEXT_START_LEGACY)
    .join("--- Web Search Results via Exa (quoted)")
    .split(SEARCH_SOURCES_START)
    .join("--- Search Sources (quoted)")
    .split(SEARCH_SOURCES_START_LEGACY)
    .join("--- Exa Sources (quoted)")
    .split(SEARCH_CONTEXT_END)
    .join("--- End Web Search Results (quoted)")
    .split(BROWSE_CONTEXT_START)
    .join("--- TinyFish Agent Results (quoted)")
    .split(BROWSE_SOURCES_START)
    .join("--- Agent Sources (quoted)")
    .split(BROWSE_CONTEXT_END)
    .join("--- End TinyFish Agent Results (quoted)");
}

/**
 * Locate a marker that may appear in a current or legacy form.
 * Returns the earliest hit and the exact string that matched, so callers can
 * advance by the right length.
 */
function findMarker(
  haystack: string,
  current: string,
  legacy: string,
  from = 0,
): { index: number; marker: string } {
  const a = haystack.indexOf(current, from);
  const b = haystack.indexOf(legacy, from);
  if (a === -1) return { index: b, marker: legacy };
  if (b === -1) return { index: a, marker: current };
  return a <= b ? { index: a, marker: current } : { index: b, marker: legacy };
}

export function parseUserContent(content: string) {
  const start = findMarker(content, SEARCH_CONTEXT_START, SEARCH_CONTEXT_START_LEGACY);
  const startIndex = start.index;
  // A real block is spliced as `contextText + text`, so it always opens the
  // message. Requiring that stops a message the user merely *typed* both
  // markers into from being treated as a search turn — which truncated their
  // question in the bubble and the thread title, and (via retryPlan) would
  // spend a paid Exa search on retry.
  const opensMessage =
    startIndex !== -1 && content.slice(0, startIndex).trim() === "";
  // Bounded scan: the first end marker at or after the block's own start.
  // Not `lastIndexOf` — that anchored on an occurrence inside the user's
  // question and collapsed the prompt to its tail. Bodies are defused by
  // `neutralizeSearchMarkers` at write time, so the first hit is the real
  // terminator.
  const endIndex =
    !opensMessage
      ? -1
      : content.indexOf(
          SEARCH_CONTEXT_END,
          startIndex + start.marker.length,
        );

  // Require a well-formed block: both markers present and in order. A
  // stray end-marker inside a normal message must not be treated as a
  // search block, or the "prompt" would be the tail of the user's text.
  if (opensMessage && endIndex > startIndex) {
    const raw = content.slice(startIndex + start.marker.length, endIndex);
    const prompt = content.slice(endIndex + SEARCH_CONTEXT_END.length).trim();

    // The citation payload is machine-only — keep it out of `context`, which
    // is what the collapsible disclosure renders.
    const src = findMarker(raw, SEARCH_SOURCES_START, SEARCH_SOURCES_START_LEGACY);
    const payloadAt = src.index;
    const context = (payloadAt === -1 ? raw : raw.slice(0, payloadAt)).trim();
    const payload =
      payloadAt === -1
        ? { provider: null, sources: [] }
        : parseSourcePayload(raw.slice(payloadAt + src.marker.length));

    return {
      hasContext: true,
      context,
      prompt,
      sources: payload.sources,
      provider: payload.provider,
    };
  }

  const browseStart = content.indexOf(BROWSE_CONTEXT_START);
  const browseOpens =
    browseStart !== -1 && content.slice(0, browseStart).trim() === "";
  const browseEnd = browseOpens
    ? content.indexOf(
        BROWSE_CONTEXT_END,
        browseStart + BROWSE_CONTEXT_START.length,
      )
    : -1;
  if (browseOpens && browseEnd > browseStart) {
    const raw = content.slice(
      browseStart + BROWSE_CONTEXT_START.length,
      browseEnd,
    );
    const prompt = content.slice(browseEnd + BROWSE_CONTEXT_END.length).trim();
    const payloadAt = raw.indexOf(BROWSE_SOURCES_START);
    const context = (payloadAt === -1 ? raw : raw.slice(0, payloadAt)).trim();
    const payload =
      payloadAt === -1
        ? { provider: BROWSE_CONTEXT_PROVIDER, sources: [] as SearchSource[] }
        : parseBrowsePayload(raw.slice(payloadAt + BROWSE_SOURCES_START.length));
    return {
      hasContext: true,
      context,
      prompt,
      sources: payload.sources,
      provider: payload.provider,
    };
  }

  return {
    hasContext: false,
    context: "",
    prompt: content,
    sources: [],
    provider: null,
  };
}


/** What a retry of a given persisted turn should do. */
export interface RetryPlan {
  draft: string;
  rearmSearch: boolean;
  rearmBrowse: boolean;
  /** Provider that produced the original block, or null if unrecorded. */
  provider: string | null;
}

// Decide how to retry a turn. A persisted `/search` or `/browse` turn stores
// `contextText + text`; re-sending that verbatim would push the grounding
// block back through the composer. Re-arm the same modality instead.
export function retryPlan(content: string): RetryPlan {
  const parsed = parseUserContent(content);
  if (!parsed.hasContext) {
    return { draft: content, rearmSearch: false, rearmBrowse: false, provider: null };
  }
  if (parsed.provider === BROWSE_CONTEXT_PROVIDER) {
    return {
      draft: parsed.prompt,
      rearmSearch: false,
      rearmBrowse: true,
      provider: parsed.provider,
    };
  }
  return {
    draft: parsed.prompt,
    rearmSearch: true,
    rearmBrowse: false,
    provider: parsed.provider,
  };
}

/** One citation from a search turn, as Exa returned it. */
export interface SearchSource {
  /**
   * 1-based position in Exa's original result list — the same number the
   * prose used. Carried explicitly rather than re-derived, because entries
   * with no usable URL are dropped: renumbering would silently relabel
   * result #2 as `[1]` and contradict citations the assistant already gave.
   */
  n: number;
  title?: string;
  url: string;
}

// Exa is asked for 6; cap above that as a cheap bound on a malformed payload.
const MAX_SOURCES = 10;

// `undefined` reaches the wire as a literal when Exa omits a field. Treat it
// as absent rather than showing the model the string "undefined".
const isUsable = (v: unknown): v is string =>
  typeof v === "string" && v.trim().length > 0 && v.trim() !== "undefined";

/** Normalize an untrusted list of `{title, url}` into usable citations. */
function toSources(raw: unknown): SearchSource[] {
  if (!Array.isArray(raw)) return [];
  const out: SearchSource[] = [];
  for (const entry of raw) {
    if (out.length >= MAX_SOURCES) break;
    if (typeof entry !== "object" || entry === null) continue;
    const { n, title, url } = entry as {
      n?: unknown;
      title?: unknown;
      url?: unknown;
    };
    if (!isUsable(url)) continue;
    // Fall back to source order when a payload predates `n` or carries junk.
    const num =
      typeof n === "number" && Number.isInteger(n) && n > 0
        ? n
        : out.length + 1;
    out.push(
      isUsable(title)
        ? { n: num, title: title.trim(), url: url.trim() }
        : { n: num, url: url.trim() },
    );
  }
  return out;
}

// A malformed payload is not an error worth surfacing — callers fall back to
// a full strip, which is the safe default.
interface SourcePayload {
  provider: string | null;
  sources: SearchSource[];
}

/**
 * Read the citation payload.
 *
 * Two shapes are accepted: the current `{provider, sources}` object, and the
 * bare `sources` array written before provider identity was recorded. Legacy
 * rows keep their citations and simply report an unknown provider — the
 * alternative, treating them as unparseable, would silently drop grounding
 * from every thread already in the database.
 *
 * A malformed payload is not an error worth surfacing; callers fall back to a
 * full strip, which is the safe default.
 */
function parseSourcePayload(payload: string): SourcePayload {
  try {
    const raw: unknown = JSON.parse(payload.trim());
    if (Array.isArray(raw)) {
      return { provider: null, sources: toSources(raw) };
    }
    if (typeof raw === "object" && raw !== null) {
      const { provider, sources } = raw as {
        provider?: unknown;
        sources?: unknown;
      };
      return {
        provider: typeof provider === "string" && provider !== "" ? provider : null,
        sources: toSources(sources),
      };
    }
    return { provider: null, sources: [] };
  } catch {
    return { provider: null, sources: [] };
  }
}

function parseBrowsePayload(payload: string): SourcePayload {
  try {
    const raw: unknown = JSON.parse(payload.trim());
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
      return { provider: BROWSE_CONTEXT_PROVIDER, sources: [] };
    }
    const { urls } = raw as { urls?: unknown };
    const list = Array.isArray(urls) ? urls : [];
    const sources: SearchSource[] = [];
    for (const entry of list) {
      if (sources.length >= MAX_SOURCES) break;
      if (!isUsable(entry)) continue;
      sources.push({ n: sources.length + 1, url: entry.trim() });
    }
    return { provider: BROWSE_CONTEXT_PROVIDER, sources };
  } catch {
    return { provider: BROWSE_CONTEXT_PROVIDER, sources: [] };
  }
}

// Assemble the block that `/search` splices ahead of the user's prompt:
// human-readable results for the disclosure, then a machine-readable
// citation payload for the replay. Lives here rather than in the component
// so the writer and `parseUserContent` cannot drift apart.
//
// `formattedResults` must already be neutralized by the caller — it is
// third-party page text.
export function buildSearchContext(
  formattedResults: string,
  sources: SearchSource[],
  provider?: string,
): string {
  // Neutralize the serialized payload, not just the prose: `JSON.stringify`
  // does not escape `-`, so an Exa title like
  // `--- End Web Search Results --- explained` would otherwise emit a live
  // sentinel *inside* the block and truncate it mid-JSON — taking `sources`,
  // the rendered question, the thread title and the retry draft with it.
  // Replacing sentinels inside JSON string values keeps the JSON valid.
  const payload = neutralizeSearchMarkers(
    JSON.stringify({
      // Recording which provider produced the block is what lets a retry
      // re-run the *same* search. Without it a retry had to guess, which
      // mislabelled the tool-call card and could prompt for consent to a
      // provider the user never chose.
      provider,
      sources: toSources(sources).map((s) =>
        s.title === undefined
          ? { n: s.n, url: s.url }
          : { n: s.n, title: s.title, url: s.url },
      ),
    }),
  );
  return [
    SEARCH_CONTEXT_START,
    SEARCH_CONFLICT_HINT,
    formattedResults,
    SEARCH_SOURCES_START,
    payload,
    SEARCH_CONTEXT_END,
    "",
    "",
  ].join("\n");
}

/** Splice TinyFish Agent extraction ahead of the user's prompt for the LLM. */
export function buildBrowseContext(markdown: string, urls: string[]): string {
  const payload = neutralizeSearchMarkers(
    JSON.stringify({
      provider: BROWSE_CONTEXT_PROVIDER,
      urls: urls.filter((u) => typeof u === "string" && u.trim() !== ""),
    }),
  );
  return [
    BROWSE_CONTEXT_START,
    BROWSE_HINT,
    neutralizeSearchMarkers(markdown),
    BROWSE_SOURCES_START,
    payload,
    BROWSE_CONTEXT_END,
    "",
    "",
  ].join("\n");
}

// Replace the spliced-in results with their citation list — titles + URLs,
// no page text — when assembling *prior* turns into the LLM request.
//
// The middle ground between replaying the whole block (which re-pays ~6
// results of page text on every later turn) and dropping it outright (which
// left "what was the URL of the second result?" unanswerable). Citations come
// from the payload, never from re-parsing the results: bodies are up to 1500
// chars of arbitrary fetched page text, and the payload's own sentinel is
// defused inside them, so a hostile page can neither forge an entry nor close
// the block early.
//
// Blocks written before the payload existed carry no sources and degrade to a
// full strip — the prior behavior.
export function compactSearchContext(content: string): string {
  const parsed = parseUserContent(content);
  if (!parsed.hasContext) return content;
  if (parsed.provider === BROWSE_CONTEXT_PROVIDER) {
    if (parsed.sources.length === 0) return parsed.prompt;
    const lines = parsed.sources.map((s) => `[${s.n}] ${s.url}`);
    return `--- Agent sources (extracted JSON omitted) ---\n${lines.join(
      "\n",
    )}\n--- End sources ---\n\n${parsed.prompt}`;
  }
  if (parsed.sources.length === 0) return parsed.prompt;

  const lines = parsed.sources.map(
    (s) => `[${s.n}] ${s.title === undefined ? "" : `${s.title} — `}${s.url}`,
  );
  return `--- Web search sources (page text omitted) ---\n${lines.join(
    "\n",
  )}\n--- End sources ---\n\n${parsed.prompt}`;
}

// Answer-first proxy for the locked HTML's `.turn-answer-headline`.
// PR-C3 layers an explicit lead-claim prefix recognizer on top of the
// older heuristic. If the model emits a `**Lead:**` / `**Answer:**` /
// `**TL;DR:**` bold-prefixed first line, we honor it verbatim. Otherwise
// we fall back to first-paragraph / first-sentence detection.
//
// Recognized prefixes (case-insensitive, with optional colon):
//   **Lead** / **Lead:** / **Answer:** / **TL;DR:** / **Bottom line:**
const LEAD_PREFIX_RE =
  /^\*\*\s*(?:lead|answer|tl;?dr|bottom line)\s*:?\s*\*\*\s*/i;

// `/image` (Gemini) replies are pure markdown images:
//   `![Nano Banana 2 Image](data:image/png;base64,<long-payload>)`
// They have no newline, no sentence terminator, no Lead prefix — so
// they fall through every split heuristic below and end up dumped
// into `headline`, which is rendered as plain JSX text and never
// reaches MarkdownView. Detect them up front and route the whole
// thing to body so the image actually renders.
const IMAGE_ONLY_RE = /^!\[[^\]]*\]\([^)]+\)\s*$/;

// A "structural-first" answer opens with a block-level markdown construct
// instead of a prose sentence: a ``` / ~~~ code fence, a `|` table row, a
// `#` heading, a `-`/`*`/`+` or `1.`/`1)` list item, a `>` blockquote, or a
// `---`/`***`/`___` thematic break. These have no prose headline to extract.
// Splitting at the first newline would tear the opening construct into
// `headline` (rendered as literal plain-text — `| Config |`, `- item`, `##
// Heading`) and leave the body unparseable (a table missing its header row, a
// fence missing its opener), so the whole answer renders as plain text.
// Keep it intact as the body so MarkdownView renders it.
//
// Marker-vs-emphasis precision: list/heading alternatives require the trailing
// space CommonMark mandates, so `**bold**` / `***x***` / `#1 priority` leads
// (no space after the marker) still flow to the prose heuristics below.
// (Code-fence variant first fixed in PR #116; generalized after the
// table/list/heading variants reproduced in 2026-05-29 dogfooding.)
const STRUCTURAL_FIRST_RE =
  /^(?:```|~~~|\||>|#{1,6}(?:\s|$)|[-*+]\s|\d+[.)]\s|---|\*\*\*|___)/;

// Defensive: strip inline **bold** AND *italic* markup from the headline
// string. The headline renders as plain JSX text (see
// ChatPage.tsx:turn-answer-headline), so any unstripped `**term**` or
// `*term*` shows the literal asterisks. The system prompt forbids
// markdown in the opening sentence, but the model violates this rule in
// practice on both emphases (e.g. "**greenhouse-gas emissions**" and
// "the *why* of scientific goals"). The body is unaffected — MarkdownView
// renders emphasis there correctly.
//
// Order matters: strip bold (`**…**`) first, then italic (`*…*`). After
// bold-strip every remaining `*X*` pair must be italic, so a simple
// non-greedy alternation suffices.
function stripInlineEmphasis(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*([^*\n]+?)\*/g, "$1");
}

function split(
  headline: string,
  body: string,
): { headline: string; body: string } {
  return { headline: stripInlineEmphasis(headline), body };
}

// Upper bound on a headline. The headline renders as plain JSX text (no
// markdown), so anything longer — or anything multi-line — must go to the body
// instead, where MarkdownView formats it. A topic sentence can legitimately run
// a few hundred chars, so this is generous; it only fires to stop a whole
// multi-paragraph answer from collapsing into a flattened plain-text headline.
const MAX_HEADLINE_LEN = 400;

export function splitAssistantContent(content: string): {
  headline: string;
  body: string;
} {
  const trimmed = content.trim();
  if (!trimmed) return split("", "");

  if (IMAGE_ONLY_RE.test(trimmed)) {
    return split("", trimmed);
  }

  // A structural-first answer (code fence, table, heading, list, blockquote,
  // thematic break) has no prose headline — keep it whole in the body so
  // MarkdownView can render it. See STRUCTURAL_FIRST_RE for the why.
  if (STRUCTURAL_FIRST_RE.test(trimmed)) {
    return split("", trimmed);
  }

  // Explicit lead-claim marker takes precedence over heuristics.
  const leadMatch = trimmed.match(LEAD_PREFIX_RE);
  if (leadMatch) {
    const afterPrefix = trimmed.slice(leadMatch[0].length);
    const nl = afterPrefix.indexOf("\n");
    if (nl > 0) {
      return split(
        afterPrefix.slice(0, nl).trim(),
        afterPrefix.slice(nl).trim(),
      );
    }
    const sent = afterPrefix.match(/^([^.!?]+[.!?])(\s|$)/);
    if (sent?.[1]) {
      return split(sent[1].trim(), afterPrefix.slice(sent[1].length).trim());
    }
    return split(afterPrefix.trim(), "");
  }

  // Prefer a paragraph break early in the response. A topic sentence can run
  // a few hundred chars, so allow up to MAX_HEADLINE_LEN before the first
  // newline — the earlier 240 cap pushed a 261-char intro into the sentence
  // split below, which then failed and dumped the whole answer into the
  // headline (2026-06-08 RAG run-on regression).
  const nl = trimmed.indexOf("\n");
  if (nl > 0 && nl <= MAX_HEADLINE_LEN) {
    return split(trimmed.slice(0, nl).trim(), trimmed.slice(nl).trim());
  }

  // Fall back to a sentence break. The "not a terminator" class also accepts a
  // `.`/`!`/`?` that is NOT followed by whitespace, so mid-token periods like
  // "Claude 3.x" or "GPT-4.1" don't prematurely end — and block — the match.
  const sentence = trimmed.match(/^((?:[^.!?]|[.!?](?!\s)){30,}?[.!?])(\s)/);
  if (sentence?.[1] && sentence[1].length <= MAX_HEADLINE_LEN) {
    return split(sentence[1], trimmed.slice(sentence[1].length).trim());
  }

  // No clean, short headline found. A multi-line or long answer must NEVER be
  // dropped into the headline — it renders as plain JSX text, flattening every
  // newline, bullet, and heading into a run-on wall. Render it as the body so
  // MarkdownView formats it. Only a genuinely short, single-line claim stays
  // as a bare headline (preserves the answer-first emphasis for terse replies).
  if (trimmed.includes("\n") || trimmed.length > MAX_HEADLINE_LEN) {
    return split("", trimmed);
  }
  return split(trimmed, "");
}

// Substantive one-line preview for the reasoning channel's collapsed
// state. Honors a `**Summary:**` bold-prefixed first line if the model
// emits one; otherwise extracts the first complete sentence (capped at
// 90 chars). Falls back to first line, then to a word-count badge.
const REASONING_SUMMARY_PREFIX_RE =
  /^\*\*\s*(?:summary|tl;?dr)\s*:?\s*\*\*\s*/i;

export function reasoningPreview(text: string): string {
  const t = text.trim();
  if (!t) return "expand";

  const match = t.match(REASONING_SUMMARY_PREFIX_RE);
  if (match) {
    const after = t.slice(match[0].length);
    const firstLine = after.split(/\n/)[0]?.trim() ?? "";
    if (firstLine) return firstLine;
  }

  // Prefer the first complete sentence if it fits on one row.
  const sentence = t.match(/^[^.!?\n]{1,90}[.!?]/);
  if (sentence) return sentence[0];

  const firstLine = t.split(/\n/)[0] ?? "";
  if (firstLine.length > 0 && firstLine.length < 90) return firstLine;

  const words = t.split(/\s+/).filter(Boolean).length;
  return `${words} words`;
}

// True while an assistant turn is mid-flight but has not yet produced
// any visible answer text — the "reasoning gap" the user stares at after
// hitting send. Deliberately independent of `reasoning`: gpt-oss-120b's
// harmony parser only emits `reasoning_content` on some turns (~30%
// baseline failure per CLAUDE.md, plus short prompts skip the analysis
// channel), so gating a progress indicator on reasoning text makes it
// flicker in and out. Gating on `pending && empty content` is
// deterministic on every turn. Once the first content token lands, the
// answer headline owns the slot and this flips false even if reasoning
// is still streaming below.
export function isThinking(assistant: ChatTurn | null | undefined): boolean {
  if (!assistant?.pending) return false;
  return (assistant.content ?? "").trim().length === 0;
}


// Decide which turns to paint. Order of precedence:
//   1. the live stream's turns, when a stream targets the thread we're viewing
//      (lets navigate-away → back resume seeing in-flight patches);
//   2. otherwise localTurns, but ONLY when they belong to the current thread.
//
// Step 2's guard is the fix for the thread-switch stale flash: Solid's
// `createResource` keeps returning the previous thread's transcript while it
// refetches the new one, so `localTurns` lags a switch by ~1s. Without gating
// on the owning thread id, thread A's content paints under thread B's URL
// until B resolves. Returning [] during that window lets the caller show a
// loading affordance instead of stale content.
export function selectVisibleTurns<T>(
  streamTurns: T[] | null,
  localTurns: T[],
  localTurnsThreadId: string | null,
  threadId: string | null,
): T[] {
  if (streamTurns) return streamTurns;
  if (localTurnsThreadId === threadId) return localTurns;
  return [];
}

export type ChatViewMode = "turns" | "loading" | "empty";

// Pick the chat surface's display mode. "loading" is distinct from "empty" so
// switching into an existing thread shows a brief loading placeholder (composer
// pinned at the bottom) rather than the centered new-conversation layout, which
// would jolt the composer to center and back. "empty" covers both the /chat
// no-thread state and a genuinely empty existing thread (settled, zero turns).
export function resolveChatViewMode(
  visibleTurnCount: number,
  threadId: string | null,
  transcriptLoading: boolean,
): ChatViewMode {
  if (visibleTurnCount > 0) return "turns";
  if (threadId && transcriptLoading) return "loading";
  return "empty";
}

// Replace inline image data: URIs (`![alt](data:image/...;base64,<huge>)`,
// produced by /image generation) with a lightweight `[generated image]`
// marker. Used when assembling prior turns into the LLM request: the raw
// base64 payload is hundreds of KB, so sending it verbatim as context blows
// gpt-oss's window / breaks the harmony parser — the follow-up then fails,
// never persists, and the user's question silently vanishes. The model still
// has the original /image *prompt* (the prior user turn) for context, so the
// marker is enough. Only `data:` URIs are stripped; normal http(s) image
// links are small and left intact. Persistence is unaffected — the stored
// turn keeps its full data URI; only the LLM-bound copy is sanitized.
export function stripImageDataUris(content: string): string {
  // Case-insensitive on `data:` — generated URIs are lowercase, but a stray
  // `DATA:` shouldn't leak the blob through.
  return content.replace(/!\[[^\]]*\]\(\s*data:[^)]*\)/gi, "[generated image]");
}

export function summarizeThread(turns: ChatTurn[]): ThreadSummary {
  const firstUserTurn = turns.find((turn) => turn.role === "user");
  const parsed = parseUserContent(firstUserTurn?.content || "");
  const title = parsed.prompt.trim() || "new thread";
  return {
    title,
    turns: turns.length,
    userTurns: turns.filter((turn) => turn.role === "user").length,
    assistantTurns: turns.filter((turn) => turn.role === "assistant").length,
    streaming: turns.some((turn) => turn.pending),
    approxTokens: turns.reduce(
      (sum, turn) =>
        sum + estimateTokens(turn.content) + estimateTokens(turn.reasoning),
      0,
    ),
  };
}
